import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Col, DatePicker, Input, Layout, Row, Select } from 'antd';

import ReturnService from 'services/return';

import { ProfileContext } from 'contexts/profile';
import { TableList } from 'components/table-list';
import PdButton from 'components/pd-button';

import { errHandling } from 'utils';
import _, { get } from 'lodash';
import addDay from 'utils/addDay';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import queryString from 'query-string';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Search } = Input;
const { Content } = Layout;

export const RentReturns = () => {
  const [endDatePick, setEndDate] = useState('');
  const [loadStatus, setLoadStatus] = useState(true);
  const [nextPage, setNextPage] = useState(1);
  const [returnList, setReturnList] = useState([]);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [sortBy, setSortBy] = useState('ordered_at desc');
  const [startDatePick, setStartDate] = useState('');
  const location = useLocation();

  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);
  const dateFormat = 'YYYY/MM/DD';

  const { fetchProfile } = useContext(ProfileContext);

  const customElement = (row) => (
    <div className="button-table">
      <Link to={`/rent_returns/detail?id=${row.id}`}>
        <PdButton type="primary" style={{ height: 32, width: 100 }}>
          See Detail
        </PdButton>
      </Link>
    </div>
  );

  customElement.displayName = 'customButton';

  const [columns] = useState([
    {
      name: 'Order No.',
      selector: row => row.formatted_id,
      width: '10%',
    },
    {
      name: 'Date',
      selector: row => row.ordered_at,
      format: (row) => {
        return dayjs(row.ordered_at).format('DD/MM/YYYY');
      },
      width: '15%',
      center: true,
    },
    {
      name: 'Customer',
      selector: row => row.user.full_name,
      width: '20%',
    },
    {
      name: 'Courier',
      selector: row => row.courier.title,
      width: '20%',
    },

    {
      name: 'Status',
      selector: row => row.status,
      format: (row) => {
        if (row.status && row.status.includes('_'))
          return _.startCase(_.toLower(row.status));
        else if (row.status) return _.capitalize(row.status);
        else return '';
      },
      width: '15%',
      center: true,
    },
    {
      name: '',
      cell: customElement,
      center: true,
      width: '20%',
    },
  ]);

  async function fetchReturnList({
    page,
    limit,
    q,
    sortBy,
    startDate,
    endDate,
  }) {
    setLoadStatus(true);
    let newEndDate = endDate;
    if (newEndDate) newEndDate = addDay(endDate, 1);

    try {
      const { data } = await ReturnService.getRentReturnList({
        page,
        limit,
        q,
        sortBy,
        startDate,
        endDate: newEndDate,
      });
      const returnData = data.data;
      const metadata = data.metadata;
      setNextPage(metadata.next_cursor);
      setLoadStatus(false);
      setReturnList(returnData);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/rent_returns',
      search: stringified,
    });
  }

  function onChangeDate(dates, datesString) {
    const startDate = datesString[0];
    if (!startDate) delete parsed['startDate'];
    else parsed['startDate'] = startDate;

    const endDate = datesString[1];
    if (!endDate) delete parsed['endDate'];
    else parsed['endDate'] = endDate;

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/rent_returns',
      search: stringified,
    });
  }

  function filterOrder(param, val) {
    if (!val) delete parsed[param];
    else parsed[param] = val;

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/rent_returns',
      search: stringified,
    });
  }

  function resetFilter() {
    const stringified = queryString.stringify(
      _.omit(parsed, [
        'page',
        'q',
        'status',
        'sortBy',
        'startDate',
        'endDate',
        'limit',
      ])
    );
    navigate({
      pathname: '/rent_returns',
      search: stringified,
    });
  }

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable */
  useEffect(() => {
    if (parsed['q']) setSearchValue(parsed['q']);
    else setSearchValue('');

    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/rent_returns',
          search: stringified,
        });
      } else setPage(parsed['page']);
    } else setPage(1);

    if (parsed['sortBy']) setSortBy(parsed['sortBy']);
    else setSortBy('ordered_at desc');

    if (parsed['startDate']) setStartDate(parsed['startDate']);
    else setStartDate('');

    if (parsed['endDate']) setEndDate(parsed['endDate']);
    else setEndDate('');

    fetchReturnList({
      page: parsed['page'],
      limit: 10,
      q: parsed['q'],
      sortBy: parsed['sortBy'],
      startDate: parsed['startDate'],
      endDate: parsed['endDate'],
    });
  }, [
    parsed['page'],
    parsed['q'],
    parsed['sortBy'],
    parsed['startDate'],
    parsed['endDate'],
  ]);
  /* eslint-enable */

  return (
    <Layout className="pd-cms-returns main-container">
      <div className="content">
        <div className="pageTitle">Rent Returns</div>
        <Content className="pageContent">
          <Row gutter={18}>
            <Col span={14}>
              <Search
                value={searchValue}
                onChange={(event) =>
                  setSearchValue(get(event, 'target.value', event))
                }
                enterButton
                className="search-product"
                placeholder="Search Return List"
                onSearch={filterOrder.bind(this, 'q')}
              />
            </Col>
            <Col span={24}>
              <Row gutter={18}>
                <Col span={7} className="mt-1">
                  <RangePicker
                    className="w-full"
                    format={dateFormat}
                    onChange={onChangeDate}
                    value={[
                      startDatePick && dayjs(startDatePick, dateFormat),
                      endDatePick && dayjs(endDatePick, dateFormat),
                    ]}
                  />
                </Col>
                <Col span={3} className="mt-1">
                  <Select
                    value={sortBy}
                    style={{ width: '100%' }}
                    onChange={filterOrder.bind(this, 'sortBy')}>
                    <Option value="ordered_at desc">Latest</Option>
                    <Option value="ordered_at asc">Oldest</Option>
                    <Option value="status">Status</Option>
                  </Select>
                </Col>
                <Col span={4} className="mt-1">
                  <span className="reset-filter" onClick={resetFilter}>
                    Reset Filter
                  </span>
                </Col>
              </Row>
            </Col>

            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                nextPage={nextPage}
                orderList={returnList}
                page={page}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
    </Layout>
  );
};

export default { RentReturns };
